<template>
  <div class="flights">
    <div v-if="seatTakenMessage" class="infomsg">{{ seatTakenMessage }}</div>
    <div v-if="!isLoading && dataFlights.length > 0" class="flights-group">
      <div v-if="!isFinished" class="flight-price-load infomsg">
        <span>You can browse, filter and sort flights while we load all prices...</span>
        <div class="loading" />
      </div>
      <pre v-if="props.isDev" class="dev">PROVIDER STATS: {{ stats }}</pre>
      <div v-if="isFinished && isLander && wantsChaperoned && !hasChaperoned && dataFlights && dataFlights.length > 0" class="infomsg">We couldn't find any {{ groupFlightLabel.toLowerCase() }} flight options with your home airport selection. This does not mean the {{ groupFlightLabel.toLowerCase() }} flight option is not available. You can explore {{ individualFlightLabel.toLowerCase() }} flights below or select to work with a travel expert in the options above to view the {{ groupFlightLabel.toLowerCase() }} trip option.</div>

      <Flight v-for="flight in flightsToDisplay" v-bind="$attrs" :key="'leg' + legIndex + flight.signature" :flight="flight" :flight-cabin="flights.cabin" :flight-way="flights.way" :is-result="true" :is-finished="isFinished" :is-dev="props.isDev" :is-lander="props.isLander" :program="props.program" :wants-chaperoned="props.wantsChaperoned" :filter="filter" :validating-signature="validatingSignature" @toggle-details="val => { if (val) hasExpandedFlight = true; }" @select-flight="selectFlight" @scroll="$emit('scroll', $event)" />
      <div v-if="hiddenFlightsCount > 0" class="more-flights">
        <a @click="showAllFlights = true">See {{ hiddenFlightsCount }} More Flights</a>
      </div>
      <div v-if="flightsFilteredAndSorted.length == 0 && filter != 0 && !isLoading && isFinished" class="no-flights">No flights match your route.{{ "\xa0" }}<a @click="reset">Reset your filter</a>.</div>
    </div>
    <div v-if="!isFinished && (isLoading || dataFlights.length == 0)" class="flights-loading">
      <div>Just one moment while we search hundreds of sources...</div>
      <div class="loading">&nbsp;</div>
    </div>
    <div v-if="isFinished && (!dataFlights || dataFlights.length == 0)" class="flights-loading">
      <p>Your search returned no results.</p>
      <p v-if="isLander">Please adjust your search or request a travel expert to get in touch with a custom quote.</p>
      <p v-else>Please consider an <a @click="convert">Assisted Booking</a> where our team will conduct a thorough search of all providers.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, inject, nextTick } from "vue";
import Flight from "./TripNewBookFlightsItem.vue";
import DateTime from "../utils/datetime";

const { utilDateFlightSearchAPI } = DateTime();
const props = defineProps(["filter", "flights", "legIndex", "organizationId", "sort", "isLander", "wantsChaperoned", "program", "inboundTo", "isDev", "cacheBust"]);
const emit = defineEmits(["error", "resetFilterSort", "scroll", "update:flights", "update:isSavedFlights", "update:isSavedRoute", "update:isSelfBook", "update:legIndex", "reqAssistedBooking", "searchResultsAvailable", "no-flights-found"]);

const axios = inject("axios");
const jwt = inject("jwt");
const groupFlightLabel = inject("groupFlightLabel", ref("Chaperoned"));
const individualFlightLabel = inject("individualFlightLabel", ref("Unchaperoned"));

const stats = ref(null);
const dataFlights = ref([]);
const failedOfferIds = new Set();
const hasExpandedFlight = ref(false);
const flightSearchId = ref(null);
const flightSearchInterval = ref(null);
const flightSearchTimeout = ref(null);
const isLoading = ref(true);
const isFinished = ref(false);
const hasChaperoned = ref(false);
const showAllFlights = ref(false);
const validatingSignature = ref(null);
const seatTakenMessage = ref(null);
const noCache = ref(false);

const convert = () => {
  if (props.flights?.route) {
    props.flights.route.forEach(leg => {
      leg.ideal = {};
    });
  }
  emit("update:isSelfBook", false);
};

const flightsFilteredAndSorted = computed(() => {
  let flights = dataFlights.value.filter(f => f.legs?.length);

  if (!flights || flights.length == 0 || !props.filter) {
    return [];
  }

  // Filtering - Name
  if (props.filter.name != "") {
    const name = props.filter.name.toLowerCase();
    flights = flights.filter(flight => {
      let isMatch = false;
      flight.legs.forEach(leg => {
        if (leg.airline_title.toLowerCase().indexOf(name) > -1) {
          isMatch = true;
        }
      });
      return isMatch;
    });
  }

  // Filter - Depart
  switch (props.filter.depart) {
    case "morning":
      flights = flights.filter(flight => {
        const depart = flight.legs[0].time_depart;
        const hour = depart.substring(11, 13);
        return hour <= 12;
      });
      break;
    case "afternoon":
      flights = flights.filter(flight => {
        const depart = flight.legs[0].time_depart;
        const hour = depart.substring(11, 13);
        return hour >= 12 && hour <= 18;
      });
      break;
    case "evening":
      flights = flights.filter(flight => {
        const depart = flight.legs[0].time_depart;
        const hour = depart.substring(11, 13);
        return hour >= 18;
      });
      break;
    default:
  }

  // Filtering - Stops
  if (props.filter.stops) {
    flights = flights.filter(flight => {
      return flight.legs.length - 1 <= Number(props.filter.stops);
    });
  }

  // Filter - policy
  if (props.filter.policy == "true") {
    flights = flights.filter(flight => flight.price_display.is_policy_within);
  }

  // Filter - policy
  if (props.filter.chaperoned == "Chaperoned") {
    flights = flights.filter(flight => flight.is_group_flight);
  }

  // Sort
  switch (props.sort) {
    case "recommended":
      // Backend calculates recommend_priority: chaperoned by price, then cheapest non-chaperoned, then rest by warnings/stops/price
      flights.sort((a, b) => a.recommend_priority - b.recommend_priority);
      break;

    case "shortest":
      flights.sort((a, b) => {
        // Place prefered Chaperoned/Individual flights at the top
        if (props.filter.chaperoned != "Undecided") {
          if (a.is_group_flight && !b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? 1 : -1;
          if (!a.is_group_flight && b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? -1 : 1;
        }
        if (a.elapsed != b.elapsed) return a.elapsed - b.elapsed;

        const aPrice = a.price_display.amount > 0 ? a.price_display.amount : 999999999;
        const bPrice = b.price_display.amount > 0 ? b.price_display.amount : 999999999;

        return aPrice - bPrice;
      });
      break;

    case "price-lowest":
      flights.sort((a, b) => {
        // Place prefered Chaperoned/Individual flights at the top
        if (props.filter.chaperoned != "Undecided") {
          if (a.is_group_flight && !b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? 1 : -1;
          if (!a.is_group_flight && b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? -1 : 1;
        }
        const aPrice = a.price_display.amount > 0 ? a.price_display.amount : 999999999;
        const bPrice = b.price_display.amount > 0 ? b.price_display.amount : 999999999;
        return aPrice === bPrice ? a.legs[0].time_depart.localeCompare(b.legs[0].time_depart) : aPrice - bPrice;
      });
      break;

    case "price-highest":
      flights.sort((a, b) => {
        // Place prefered Chaperoned/Individual flights at the top
        if (props.filter.chaperoned != "Undecided") {
          if (a.is_group_flight && !b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? 1 : -1;
          if (!a.is_group_flight && b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? -1 : 1;
        }
        return b.price_display.amount - a.price_display.amount;
      });
      break;

    case "depart-earliest":
      flights.sort((a, b) => {
        // Place prefered Chaperoned/Individual flights at the top
        if (props.filter.chaperoned != "Undecided") {
          if (a.is_group_flight && !b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? 1 : -1;
          if (!a.is_group_flight && b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? -1 : 1;
        }
        return a.legs[0].time_depart.localeCompare(b.legs[0].time_depart);
      });
      break;

    case "depart-latest":
      flights.sort((a, b) => {
        // Place prefered Chaperoned/Individual flights at the top
        if (props.filter.chaperoned != "Undecided") {
          if (a.is_group_flight && !b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? 1 : -1;
          if (!a.is_group_flight && b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? -1 : 1;
        }
        return b.legs[0].time_depart.localeCompare(a.legs[0].time_depart);
      });
      break;

    case "arrive-earliest":
      flights.sort((a, b) => {
        // Place prefered Chaperoned/Individual flights at the top
        if (props.filter.chaperoned != "Undecided") {
          if (a.is_group_flight && !b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? 1 : -1;
          if (!a.is_group_flight && b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? -1 : 1;
        }
        return a.legs[a.legs.length - 1].time_arrive.localeCompare(b.legs[b.legs.length - 1].time_arrive);
      });
      break;

    case "arrive-latest":
      flights.sort((a, b) => {
        // Place prefered Chaperoned/Individual flights at the top
        if (props.filter.chaperoned != "Undecided") {
          if (a.is_group_flight && !b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? 1 : -1;
          if (!a.is_group_flight && b.is_group_flight) return props.filter.chaperoned != "Chaperoned" ? -1 : 1;
        }
        return b.legs[b.legs.length - 1].time_arrive.localeCompare(a.legs[a.legs.length - 1].time_arrive);
      });
      break;

    default:
  }
  return flights;
});

const flightsToDisplay = computed(() => {
  const sorted = flightsFilteredAndSorted.value;
  if (!props.isLander || showAllFlights.value || hasExpandedFlight.value) return sorted;
  const groupCount = sorted.filter(f => f.is_group_flight).length;
  return sorted.slice(0, groupCount + 5);
});

const hiddenFlightsCount = computed(() => {
  return flightsFilteredAndSorted.value.length - flightsToDisplay.value.length;
});

const signaturesAll = flight => {
  if (flight.signature_alt) return flight.signature_alt;

  let uniqueSignatures = [];
  flight.prices.forEach(price => {
    const prefix = price.signature.split("/")[0];
    if (!uniqueSignatures.includes(prefix)) uniqueSignatures.push(prefix);
  });
  return uniqueSignatures.join("|");
};

const formatDate = date => {
  // Use standardized date formatting for flight search API
  return utilDateFlightSearchAPI(date);
};

const loadFlightSearch = () => {
  // Build legs parameter: DATE:FROM-TO,DATE:FROM-TO,...
  const buildLegs = () => {
    if (!props.flights?.route?.length) return "";
    return props.flights.route
      .map(leg => {
        const date = leg.depart ? formatDate(leg.depart) : "";
        const from = leg.from?.iata || "";
        const to = leg.to?.iata || "";
        return `${date}:${from}-${to}`;
      })
      .join(",");
  };

  const legs = buildLegs();
  let url = `${import.meta.env.VITE_APP_FLIGHTFOX_API_URL}/flight-search?cabin=${props.flights.cabin}&pax=${props.flights.pax}`;

  // Add outbound flight info for multi-leg booking flow
  if (props.legIndex >= 1 && props.flights?.route?.length >= 2) {
    const outboundIdeal = props.flights.route[0]?.ideal;
    if (outboundIdeal && (outboundIdeal.signature || outboundIdeal.signature_alt)) {
      const outboundPrice = outboundIdeal.price_display ? outboundIdeal.price_display.amount : 0;
      let outboundFlight = signaturesAll(outboundIdeal);
      for (let i = 1; i < props.legIndex; i++) {
        if (props.flights.route[i]?.ideal) {
          outboundFlight += "/" + signaturesAll(props.flights.route[i].ideal);
        }
      }
      url += `&outbound-flight=${outboundFlight}&outbound-price=${outboundPrice}`;
    }
  }

  if (props.organizationId != null && props.organizationId > 0) {
    url += "&organization-id=" + props.organizationId;
  }

  if (props.program?.id) {
    url += "&event-id=" + props.program.id + "&leg-index=" + props.legIndex + "&is-arrival=true";
  }

  url += `&legs=${legs}`;

  if (noCache.value) {
    url += "&no-cache=true";
    noCache.value = false;
  }

  dataFlights.value = [];
  isLoading.value = true;

  axios(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
    },
  })
    .then(response => {
      flightSearchId.value = response.data.id;
      clearInterval(flightSearchInterval.value);
      isLoading.value = false;
      isFinished.value = false;
      hasChaperoned.value = false;
      flightSearchInterval.value = setInterval(loadFlightSearchPoll, 5000);
      flightSearchTimeout.value = setTimeout(loadFlightSearchPoll, 1000); //do it right away, just allow some minimal time for processing - cause it might be in partial cache
      setTimeout(loadFlightSearchPoll, 300); //do it right away, just allow some minimal time for processing - cause it might be in global cache
    })
    .catch(error => {
      clearTimeout(flightSearchTimeout.value);
      clearInterval(flightSearchInterval.value);
      isFinished.value = true;
      isLoading.value = false;
      emit("error", error);
    });
};

const loadFlightSearchPoll = () => {
  if (!flightSearchId.value) {
    return;
  }

  let url = `${import.meta.env.VITE_APP_FLIGHTFOX_API_URL}/flight-search/${flightSearchId.value}`;
  if (props.program?.id) {
    url += `?event-id=${props.program.id}`;
  }

  axios(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
    },
  })
    .then(response => {
      if (isLoading.value && props.legIndex === 1) {
        emit("scroll", ".flights, #ideal_flights, #flights", 500);
      }

      stats.value = response.data.flight_stats;

      const disclaimerDict = response.data.disclaimers || [];
      const flights = (response.data.flights || []).filter(f => f.legs?.length);
      flights.forEach(f => {
        if (Array.isArray(f.disclaimers)) {
          f.disclaimers = f.disclaimers.map(i => disclaimerDict[i]).filter(Boolean);
        }
      });
      dataFlights.value = flights;
      isLoading.value = false;

      if (props.isLander) {
        for (let i = 0; i < dataFlights.value.length; i++) {
          hasChaperoned.value = hasChaperoned.value || dataFlights.value[i].is_group_flight;
        }

        nextTick(() => emit("searchResultsAvailable", dataFlights.value.length));
      }

      if (response.data.is_finished === true) {
        isFinished.value = true;
        clearTimeout(flightSearchTimeout.value);
        clearInterval(flightSearchInterval.value);
      }
    })
    .catch(error => {
      if (error.request !== void 0 && error.response === void 0) {
        console.warn("Network error, continue polling");
      } else {
        isFinished.value = true;
        isLoading.value = false;
        clearTimeout(flightSearchTimeout.value);
        clearInterval(flightSearchInterval.value);
        emit("error", error);
      }
    });
};

const validateSinglePrice = async price => {
  const isDf = !!price.df_offer_id;
  const param = isDf ? `df-offer-id=${encodeURIComponent(price.df_offer_id)}` : `dtt-offer-id=${encodeURIComponent(price.dtt_offer_id)}`;
  let url = `${import.meta.env.VITE_APP_FLIGHTFOX_API_URL}/flight-validate?${param}&amount=${price.amount}`;
  if (props.program?.id) url += `&event-id=${props.program.id}`;
  const response = await axios(url, {
    headers: { Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" },
    timeout: 30000,
  });
  return isDf ? response.data.df : response.data.dtt;
};

const selectFlight = async flight => {
  if (validatingSignature.value) return;

  seatTakenMessage.value = null;

  const newFlights = props.flights;
  newFlights.route[props.legIndex].ideal = flight;
  emit("update:flights", newFlights);

  const isLastLeg = props.flights?.route ? props.legIndex == props.flights.route.length - 1 : false;

  if (!isLastLeg) {
    emit("scroll", ".flights, #ideal_flights, #flights", 500);
    emit("update:legIndex", props.legIndex + 1);
    return;
  }

  validatingSignature.value = flight.signature;

  try {
    const US_CARRIERS = new Set(["UA", "AA", "DL"]);
    const allBookable = flight.prices?.filter(p => p.is_bookable && (p.df_offer_id || p.dtt_offer_id) && !failedOfferIds.has(p.df_offer_id) && !failedOfferIds.has(p.dtt_offer_id)) || [];
    const cheapestAmount = allBookable.reduce((min, p) => !min || p.amount < min ? p.amount : min, 0);
    const bookable = allBookable.sort((a, b) => {
      const aIsUs = US_CARRIERS.has(a.airline_iata);
      const bIsUs = US_CARRIERS.has(b.airline_iata);
      if (aIsUs !== bIsUs && cheapestAmount > 0) {
        const threshold = cheapestAmount * 1.05;
        if (aIsUs && a.amount <= threshold) return -1;
        if (bIsUs && b.amount <= threshold) return 1;
      }
      return a.amount - b.amount;
    });

    let validPrice = null;
    let priceChanged = false;
    let anyApiError = false;

    for (const price of bookable) {
      try {
        const result = await validateSinglePrice(price);
        if (result?.valid) {
          validPrice = price;
          if (result.amount && result.amount !== price.amount) {
            price.amount = result.amount;
            priceChanged = true;
          }
          break;
        }
        // Not valid: offer expired, price out of tolerance, or seats gone.
        // Mark this offer ID so it won't appear in refreshed results.
        if (price.df_offer_id) failedOfferIds.add(price.df_offer_id);
        if (price.dtt_offer_id) failedOfferIds.add(price.dtt_offer_id);
      } catch (e) {
        console.warn("Offer validation error, trying next", e);
        anyApiError = true;
      }
    }

    if (validPrice) {
      if (priceChanged) {
        // Price changed within tolerance. Update the display and let the customer see the new price.
        emit("update:flights", newFlights);
      }
      emit("update:isSavedFlights", true);
    } else if (bookable.length === 0 || anyApiError) {
      // No bookable prices or API errors. Proceed (backend handles it).
      emit("update:isSavedFlights", true);
    } else {
      // All offers invalid. Reset and trigger fresh search.
      newFlights.route.forEach(leg => (leg.ideal = {}));
      emit("update:flights", newFlights);
      seatTakenMessage.value = "We're sorry, but that flight is no longer available at that price. Please select a different option.";
      noCache.value = true;
      if (props.legIndex === 0) {
        loadFlightSearch();
      } else {
        emit("update:legIndex", 0);
      }
      emit("scroll", ".flights, #ideal_flights, #flights", 0);
    }
  } catch (e) {
    console.warn("Offer validation failed, proceeding with cached prices", e);
    emit("update:isSavedFlights", true);
  }

  validatingSignature.value = null;
};

const reset = () => {
  emit("resetFilterSort");
};

watch(
  () => props.legIndex,
  (newIndex) => {
    emit("resetFilterSort");
    isFinished.value = false;
    isLoading.value = true;
    dataFlights.value = [];
    hasExpandedFlight.value = false;
    // Only load flight search if we have a valid legIndex.
    // Always defer via nextTick so a concurrent cacheBust increment (same flush)
    // can set noCache = true before loadFlightSearch reads it.
    if (newIndex >= 0) {
      nextTick(() => {
        loadFlightSearch();
      });
    }
  }
);

// Watch for no flights found in landing page searches
watch([isFinished, dataFlights], ([finished, flights]) => {
  if (finished && props.isLander && (!flights || flights.length === 0) && isLoading.value === false) {
    emit("no-flights-found");
  }
});

// Re-search when route airports change (e.g. lander home airport picker)
watch(
  () => props.flights?.route?.[0]?.from?.iata,
  (val, old) => {
    if (old && val && props.legIndex === 0) {
      loadFlightSearch();
    }
  }
);

// Trigger cache-busted fresh search when parent increments cacheBust (e.g. price/expiry error).
// Sets noCache immediately so the legIndex watcher's deferred nextTick call picks it up.
// Only calls loadFlightSearch directly if legIndex didn't change (watcher won't fire in that case).
watch(
  () => props.cacheBust,
  (val) => {
    if (val > 0) {
      noCache.value = true;
      nextTick(() => {
        // If noCache was already consumed by the legIndex watcher's nextTick, skip.
        if (noCache.value) {
          loadFlightSearch();
        }
      });
    }
  }
);

onMounted(() => {
  // Only load flight search on mount if we have a valid legIndex
  if (props.legIndex >= 0) {
    // If cacheBust is already positive on mount (component remounted after a price/expiry error),
    // the watcher won't fire for the initial value — set noCache directly here.
    if (props.cacheBust > 0) noCache.value = true;
    loadFlightSearch();
  }
});
onBeforeUnmount(() => {
  clearTimeout(flightSearchTimeout.value);
  clearInterval(flightSearchInterval.value);
});
</script>

<style lang="less">
#flights {
  .flights {
    margin-bottom: 2rem;
  }

  .flights-group.loading,
  .no-flights {
    align-items: center;
    background: @light;
    border-radius: 0.25rem;
    box-sizing: border-box;
    min-height: 4.625rem;
    padding: 1rem;
    text-align: center;
    width: 100%;

    &:after {
      background: @light !important;
    }
  }

  .flight-price-load {
    align-items: center;
    display: flex;

    span {
      flex: 1;
    }

    .loading {
      float: right;
      width: 1.25rem;

      &:after {
        background: transparent;
      }
    }
  }

  .flights-loading {
    background: @light;
    padding: 2rem;
    text-align: center;
    border-radius: 1rem;

    p {
      margin: 0 auto 2rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .loading {
      margin-top: 2rem;

      &:after {
        background: transparent;
      }
    }
  }

  .no-flights {
    color: @mid;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .more-flights {
    padding-top: 1rem;
    text-align: center;
  }

  .connections {
    display: block;
    margin-top: 2rem;
    text-align: center;

    &.expanded {
      margin-bottom: 2rem;
    }
  }
}
</style>
